/**
 * The models picker of Models → View model (Pedro 2026-11-02: "Can the model picker be exactly like the
 * Portfolio picker in View Portfolio, with all its features?"). The same look and behaviour as
 * `PortfolioPicker` in multi mode: a button that collapses to one line (the model's name, or "N
 * models"), a search box, the ticked models listed under **Selected** at the top, **All models (N)**,
 * the list in its own order, a count with **Clear**, and **Show** / **Cancel** — the ticks are a draft
 * until Show (PICK-SHOW, C-293). At most 12 models side by side (the server's limit): a 13th tick is
 * refused in red, as the portfolio picker refuses a portfolio shown twice. (`ModelPicker` is the
 * single-choice picker of Rebalance to… → Model.)
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Anchor, Button, Checkbox, Divider, Group, Popover, ScrollArea, Stack, Text, TextInput, Tooltip,
} from '@mantine/core';
import { IconAlertTriangle, IconChevronDown } from '@tabler/icons-react';

import type { ModelPortfolio } from '../api/models';

export const MAX_MODELS = 12;

export function ModelMultiPicker({ models, value, onChange }: {
  models: ModelPortfolio[];
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);
  const [search, setSearch] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  // Opening starts from what is shown; closing without Show drops the draft.
  useEffect(() => { if (open) { setDraft(value); setRefusal(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const byId = useMemo(() => Object.fromEntries(models.map((m) => [m.id, m])), [models]);
  const nameOf = (id: string) => byId[id]?.name ?? '…';
  const t = search.trim().toLowerCase();
  const items = models.filter((m) => !t || m.name.toLowerCase().includes(t) || (m.description ?? '').toLowerCase().includes(t));
  const tooMany = models.length > MAX_MODELS;
  const isAll = models.length > 0 && models.every((m) => draft.includes(m.id));
  const toggleAll = () => { setRefusal(null); setDraft(isAll ? [] : models.map((m) => m.id)); };
  const toggle = (id: string) => {
    setRefusal(null);
    if (draft.includes(id)) { setDraft((d) => d.filter((x) => x !== id)); return; }
    if (draft.length >= MAX_MODELS) {
      setRefusal(`At most ${MAX_MODELS} models side by side: ${nameOf(id)} is not added. Untick another one first.`);
      return;
    }
    setDraft((d) => [...d, id]);
  };
  const changed = draft.length !== value.length || draft.some((x) => !value.includes(x));
  const show = () => { onChange(draft); setOpen(false); };
  const summary = value.length === 0 ? 'Choose models' : value.length === 1 ? nameOf(value[0]) : `${value.length} models`;
  const tip = (m: ModelPortfolio) => [m.description, `${m.component_count} component${m.component_count === 1 ? '' : 's'}`,
    m.followed_by?.length ? `followed by ${m.followed_by.join(', ')}` : null].filter(Boolean).join(' — ');

  return (
    <Stack gap={2}>
      <Text size="xs" fw={500}>Models</Text>
      <Popover width={320} position="bottom-start" shadow="md" withArrow opened={open} onChange={setOpen} trapFocus>
        <Popover.Target>
          <Button size="xs" variant="default" w={220} justify="space-between" rightSection={<IconChevronDown size={14} />}
            onClick={() => setOpen((o) => !o)} styles={{ label: { fontWeight: value.length ? 500 : 400 } }}>
            {summary}
          </Button>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="xs">
            <TextInput size="xs" placeholder="Model name or description" value={search} onChange={(e) => setSearch(e.currentTarget.value)} />
            <ScrollArea.Autosize mah={300}>
              <Stack gap={4}>
                {/* PICK-SEL: what is ticked, at the top; unticking here unticks it below too. */}
                {draft.length > 0 && (
                  <>
                    <Text size="xs" fw={600}>Selected</Text>
                    {draft.map((id) => <Checkbox key={`sel-${id}`} size="xs" checked onChange={() => toggle(id)} label={nameOf(id)} />)}
                    <Divider my={2} />
                  </>
                )}
                {models.length > 0 && (
                  <Tooltip withArrow multiline w={280} label={tooMany
                    ? `More than ${MAX_MODELS} models: tick up to ${MAX_MODELS} one by one.`
                    : 'Tick every model, side by side.'}>
                    <Checkbox size="xs" fw={600} label={`All models (${models.length})`} checked={isAll} disabled={tooMany}
                      onChange={toggleAll} />
                  </Tooltip>
                )}
                {models.length > 0 && <Divider my={2} />}
                {items.length === 0 && <Text size="xs" c="dimmed">{models.length ? 'No model matches' : 'No model yet'}</Text>}
                {items.map((m) => (
                  <Tooltip key={m.id} withArrow multiline w={300} label={tip(m)} openDelay={400}>
                    <Checkbox size="xs" label={m.name} checked={draft.includes(m.id)} onChange={() => toggle(m.id)} />
                  </Tooltip>
                ))}
              </Stack>
            </ScrollArea.Autosize>
            {refusal && (
              <Alert color="red" variant="filled" p="xs" icon={<IconAlertTriangle size={16} />} title="Too many models"
                styles={{ body: { minWidth: 0 }, message: { overflowWrap: 'anywhere' } }}>
                <Text size="xs">{refusal}</Text>
              </Alert>
            )}
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{`${draft.length} model${draft.length === 1 ? '' : 's'} chosen`}</Text>
              {draft.length > 0 && <Anchor size="xs" onClick={() => { setDraft([]); setRefusal(null); }}>Clear</Anchor>}
            </Group>
            <Group gap="xs" justify="flex-end">
              <Button size="xs" onClick={show} disabled={!changed}>Show</Button>
              <Button size="xs" variant="default" onClick={() => setOpen(false)}>Cancel</Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Stack>
  );
}
